import { KeyRound, PauseCircle, PlayCircle, Search } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { messageLisible } from '@/shared/api/erreurs';
import {
  useReactiverUtilisateur,
  useReinitialiserMotDePasse,
  useSuspendreUtilisateur,
  useUtilisateurs,
  type UtilisateurAdminDto,
} from '@/shared/api/hooksAdmin';
import { ACTES } from '@/shared/audit/actesSensibles';
import { DialogueActeSensible } from '@/shared/audit/DialogueActeSensible';
import { date, entier } from '@/shared/lib/utils';
import { Bouton } from '@/shared/ui/Bouton';
import { Saisie } from '@/shared/ui/Champ';
import { EnTeteSection, Pastille, Table, type ColonneTable } from '@/shared/ui/Table';

type ActeCompte =
  | { type: 'suspendre'; compte: UtilisateurAdminDto }
  | { type: 'reactiver'; compte: UtilisateurAdminDto }
  | { type: 'reinitialiser'; compte: UtilisateurAdminDto };

/**
 * Comptes utilisateurs.
 *
 * AUCUN SECRET N'EST AFFICHABLE ICI, ET CE N'EST PAS UNE PRECAUTION D'INTERFACE : le DTO
 * renvoye par l'API ne contient ni mot de passe ni empreinte, et un mot de passe n'existe
 * nulle part en clair par conception d'Identity. Il n'y a donc rien a masquer — c'est la
 * forme la plus solide de la regle du §4.
 *
 * La reinitialisation elle-meme ne remonte pas son jeton : il est destine au titulaire du
 * compte. Un administrateur capable de le lire prendrait la main sur n'importe quel compte
 * sans que son titulaire le sache.
 */
export default function UtilisateursPage() {
  const [recherche, setRecherche] = useState('');
  const [acte, setActe] = useState<ActeCompte | null>(null);

  const { data, isPending, isError, error, refetch } = useUtilisateurs(recherche.trim() || undefined);

  const suspendre = useSuspendreUtilisateur();
  const reactiver = useReactiverUtilisateur();
  const reinitialiser = useReinitialiserMotDePasse();

  const colonnes: readonly ColonneTable<UtilisateurAdminDto>[] = [
    {
      cle: 'email',
      entete: 'Compte',
      rendu: (ligne) => (
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium text-texte">{ligne.email}</span>

          {ligne.estAdministrateurPlateforme ? (
            <Pastille ton="danger">Admin plateforme</Pastille>
          ) : null}

          {ligne.estAnonymise ? <Pastille ton="neutre">Anonymise</Pastille> : null}
          {ligne.estSuspendu ? <Pastille ton="alerte">Suspendu</Pastille> : null}
        </span>
      ),
    },
    {
      cle: 'nom',
      entete: 'Nom',
      secondaire: true,
      rendu: (ligne) => <span className="text-texte-doux">{ligne.nomComplet?.trim() || '—'}</span>,
    },
    {
      cle: 'entreprises',
      entete: 'Entreprises',
      numerique: true,
      rendu: (ligne) => entier(ligne.nombreEntreprises),
    },
    {
      cle: 'creeLe',
      entete: 'Cree le',
      secondaire: true,
      rendu: (ligne) => <span className="text-texte-doux">{date(ligne.creeLe)}</span>,
    },
    {
      cle: 'connexion',
      entete: 'Derniere connexion',
      secondaire: true,
      rendu: (ligne) => <span className="text-texte-doux">{date(ligne.derniereConnexionLe)}</span>,
    },
    {
      cle: 'actions',
      entete: '',
      rendu: (ligne) => (
        <div className="flex justify-end gap-1">
          <Bouton
            variante="discret"
            taille="sm"
            disabled={ligne.estAnonymise ?? false}
            onClick={() => setActe({ type: 'reinitialiser', compte: ligne })}
          >
            <KeyRound />
            Reinitialiser
          </Bouton>

          {ligne.estSuspendu ? (
            <Bouton
              variante="discret"
              taille="sm"
              onClick={() => setActe({ type: 'reactiver', compte: ligne })}
            >
              <PlayCircle />
              Reactiver
            </Bouton>
          ) : (
            <Bouton
              variante="discret"
              taille="sm"
              onClick={() => setActe({ type: 'suspendre', compte: ligne })}
            >
              <PauseCircle />
              Suspendre
            </Bouton>
          )}
        </div>
      ),
    },
  ];

  function fermer() {
    setActe(null);
  }

  return (
    <div className="space-y-4">
      <EnTeteSection
        titre="Comptes"
        description="Comptes d’acces, tous rattachements d’entreprise confondus."
        sensible="Aucun mot de passe n’est consultable : il n’en existe aucune copie en clair. Les actes ci-dessous sont journalises."
      />

      <div className="relative max-w-xs">
        <Search
          className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-texte-faible"
          aria-hidden
        />
        <Saisie
          type="search"
          value={recherche}
          onChange={(evenement) => setRecherche(evenement.target.value)}
          placeholder="Adresse e-mail…"
          aria-label="Rechercher un compte"
          className="pl-7"
        />
      </div>

      <Table
        colonnes={colonnes}
        lignes={data}
        cleLigne={(ligne) => ligne.utilisateurId ?? ''}
        chargement={isPending}
        erreur={isError ? error : undefined}
        onReessayer={() => void refetch()}
        messageVide="Aucun compte ne correspond."
      />

      {acte?.type === 'suspendre' ? (
        <DialogueActeSensible
          acte={ACTES.utilisateurSuspendu}
          cible={acte.compte.email ?? ''}
          enCours={suspendre.isPending}
          onFermer={fermer}
          onConfirmer={(motif) =>
            suspendre.mutate(
              { utilisateurId: acte.compte.utilisateurId as string, motif },
              {
                onSuccess: () => {
                  toast.success('Compte suspendu', { description: 'Sessions fermees, acte consigne.' });
                  fermer();
                },
                onError: (erreur) =>
                  toast.error('Suspension impossible', { description: messageLisible(erreur) }),
              },
            )
          }
        />
      ) : null}

      {acte?.type === 'reactiver' ? (
        <DialogueActeSensible
          acte={ACTES.utilisateurReactive}
          cible={acte.compte.email ?? ''}
          enCours={reactiver.isPending}
          onFermer={fermer}
          onConfirmer={() =>
            reactiver.mutate(acte.compte.utilisateurId as string, {
              onSuccess: () => {
                toast.success('Compte reactive');
                fermer();
              },
              onError: (erreur) =>
                toast.error('Reactivation impossible', { description: messageLisible(erreur) }),
            })
          }
        />
      ) : null}

      {acte?.type === 'reinitialiser' ? (
        <DialogueActeSensible
          acte={ACTES.reinitialisationDeclenchee}
          cible={acte.compte.email ?? ''}
          enCours={reinitialiser.isPending}
          onFermer={fermer}
          onConfirmer={() =>
            reinitialiser.mutate(acte.compte.utilisateurId as string, {
              onSuccess: () => {
                toast.success('Reinitialisation declenchee', {
                  description: 'Le lien est destine au titulaire du compte.',
                });
                fermer();
              },
              onError: (erreur) =>
                toast.error('Action impossible', { description: messageLisible(erreur) }),
            })
          }
        />
      ) : null}
    </div>
  );
}
